// src/pages/teacher/AddTestPage.jsx
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { TbSmartHome } from "react-icons/tb";
import { FiUploadCloud } from "react-icons/fi";
import PanelLayout from "layouts/PanelLayout";
import { RxCross2 } from "react-icons/rx";
import { RiUploadCloudLine } from "react-icons/ri";
import { toast } from "react-toastify";
import "styles/teacher.css";

const API_BASE = "https://algoritm-api-edu.uz";

const AddTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const subject = location.state?.subject || { fullName: "Fan nomi", slug: "" };
  const existingTest = location.state?.test;
  const isEditMode = !!location.state?.isEdit;

  const [step, setStep] = useState(1);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const [testData, setTestData] = useState({
    title: existingTest?.title || "",
    type: existingTest?.type || "Standart",
    description: existingTest?.description || "",
    time: existingTest?.allowedMinutes || "",
    count: existingTest?.attemptCount || "",
  });

  const [questions, setQuestions] = useState(
    isEditMode && existingTest?.questions && existingTest.questions.length > 0
      ? existingTest.questions.map((q) => ({
          question: q.questionText || q.question || "",
          options: q.options || ["", "", "", ""],
          correct: q.correctOptionIndex || 0,
          image: null,
        }))
      : [{ question: "", options: ["", "", "", ""], correct: 0, image: null }]
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setTestData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleNext = () => {
    if (testData.title.trim() && testData.time && testData.count) {
      setStep(2);
    } else {
      toast.warn("Majburiy maydonlarni to‘ldiring!");
    }
  };

  const handlePrev = () => setStep(1);

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      { question: "", options: ["", "", "", ""], correct: 0, image: null },
    ]);
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleOptionChange = (qIndex, optIndex, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[qIndex].options[optIndex] = value;
      return updated;
    });
  };

  const handleCorrectChange = (qIndex, value) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[qIndex].correct = parseInt(value);
      return updated;
    });
  };

  const handleSubmit = async () => {
    // Validatsiya
    if (!subject?.slug && !isEditMode) {
      toast.error("Fan slug'i mavjud emas!");
      return;
    }
    if (!testData.title.trim()) return toast.error("Test nomi kiritilmagan!");
    if (!testData.time || testData.time <= 0) return toast.error("Vaqt musbat son bo‘lishi kerak!");
    if (!testData.count || testData.count <= 0) return toast.error("Urinishlar soni musbat son bo‘lishi kerak!");
    if (questions.some((q) => !q.question.trim())) return toast.error("Barcha savollar to‘ldirilishi kerak!");

    if (
      questions.some(
        (q) =>
          q.options.filter((o) => o.trim()).length < 2 ||
          q.correct < 0 ||
          q.correct >= q.options.length
      )
    ) {
      return toast.error("Har bir savolda kamida 2 variant va to‘g‘ri javob bo‘lishi kerak!");
    }

    setLoading(true);
    const token = localStorage.getItem("accessToken");

    try {
      const formData = new FormData();

      // Edit yoki Create ga qarab to‘g‘ri fieldlar
      if (isEditMode) {
        formData.append("TestSlug", existingTest.slug);
      } else {
        formData.append("SubjectSlug", subject.slug);
      }

      formData.append("Title", testData.title.trim());
      formData.append("Description", testData.description.trim() || "");
      formData.append("AllowedMinutes", parseInt(testData.time));
      formData.append("AttemptCount", parseInt(testData.count));
      formData.append("TestTypeId", testData.type === "Logic" ? 2 : 1);

      if (image) {
        formData.append("File", image);
      }

      const url = isEditMode
        ? `${API_BASE}/api/teacher/Test/Update`
        : `${API_BASE}/api/teacher/Test/Add`;

      const method = isEditMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const responseText = await res.text();

      if (!res.ok) {
        throw new Error(responseText || "Server xatosi");
      }

      // Slug ni olish (faqat yaratishda kerak)
      let testSlug = isEditMode ? existingTest.slug : null;

      if (!isEditMode) {
        try {
          const json = JSON.parse(responseText);
          testSlug = json.slug || json.data?.slug || json.testSlug || json.Slug;
        } catch (e) {
          console.warn("Backend text qaytardi:", responseText);
          toast.info("Test yaratildi, lekin slug olinmadi.");
        }
      }

      // Savollar saqlash (har ikki holatda ham)
      if (testSlug || isEditMode) {
        const questionsPayload = {
          TestSlug: testSlug || existingTest.slug,
          Questions: questions
            .filter((q) => q.question.trim())
            .map((q) => ({
              QuestionText: q.question.trim(),
              Options: q.options.filter((o) => o.trim() !== ""),
              CorrectOptionIndex: q.correct,
            })),
        };

        const qRes = await fetch(
          `${API_BASE}/api/teacher/Test/AddOrUpdateQuestionsToTest`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(questionsPayload),
          }
        );

        if (!qRes.ok) {
          toast.warn("Savollar saqlanmadi yoki yangilanmadi.");
        } else {
          toast.success("Savollar muvaffaqiyatli saqlandi!");
        }
      }

      toast.success(isEditMode ? "Test muvaffaqiyatli yangilandi!" : "Test muvaffaqiyatli yaratildi!");
      navigate("/teacher/tests", { state: { subject } });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Xatolik yuz berdi. Backend bilan bog‘laning.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PanelLayout>
      <motion.div
        className="subject-page"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="tests-header">
          <div className="breadcrumb">
            <span onClick={() => navigate("/teacher")}>
              <TbSmartHome style={{ fontSize: 20, color: "#01CC0D", cursor: "pointer" }} />
            </span>
            <span className="divider">/</span>
            <span onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
              {subject.fullName}
            </span>
            <span className="divider">/</span>
            <span
              onClick={() => navigate("/teacher/tests", { state: { subject } })}
              style={{ cursor: "pointer" }}
            >
              Testlar
            </span>
            <span className="divider">/</span>
            <span className="current">
              {isEditMode ? "Test tahrirlash" : step === 1 ? "Test yaratish" : "Savollar qo‘shish"}
            </span>
          </div>
        </div>

        {/* Step 1: Test ma'lumotlari */}
        {step === 1 && (
          <motion.form
            className="add-test-form"
            onSubmit={(e) => e.preventDefault()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FormInput label="Nomi" name="title" value={testData.title} onChange={handleChange} required />
            <FormInput label="Urinishlar soni" name="count" value={testData.count} onChange={handleChange} required />
            <FormInput label="Daqiqa" name="time" value={testData.time} onChange={handleChange} required />

            <div className="form-group">
              <label>Test turi</label>
              <div className="radio-group">
                {["Standart", "Logic"].map((type) => (
                  <label key={type}>
                    <input
                      type="radio"
                      name="type"
                      value={type}
                      checked={testData.type === type}
                      onChange={handleChange}
                    />
                    <span>{type === "Logic" ? "Mantiqiy" : "Standart"}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-item">
              <label>Rasm {isEditMode && "(yangisini yuklasangiz almashtiriladi)"}</label>
              <div className="image-upload-box">
                <motion.div
                  className="upload-placeholder"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => document.getElementById("imageInput").click()}
                >
                  <FiUploadCloud size={22} />
                  {image ? (
                    <div className="file-info">
                      <p className="file-name">{image.name}</p>
                      <span className="remove-file" onClick={(e) => { e.stopPropagation(); setImage(null); }}>
                        ×
                      </span>
                    </div>
                  ) : (
                    <p>Rasm yuklang</p>
                  )}
                </motion.div>
                <input
                  id="imageInput"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => setImage(e.target.files[0])}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Izoh (majburiy emas)</label>
              <textarea
                name="description"
                rows="4"
                value={testData.description}
                onChange={handleChange}
                placeholder="Yozing..."
              />
            </div>

            <div className="form-actions">
              <motion.button
                type="button"
                className="save-btn"
                onClick={handleNext}
                disabled={!testData.title.trim() || !testData.time || !testData.count || loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Keyingi bosqich
              </motion.button>
              <motion.button
                type="button"
                className="cancel-btn"
                onClick={() => navigate(-1)}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Bekor qilish
              </motion.button>
            </div>
          </motion.form>
        )}

        {/* Step 2: Savollar */}
        {step === 2 && (
          <motion.div
            className="add-test-form"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {questions.map((q, i) => (
              <motion.div
                key={i}
                className="question-block new-question-ui"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="question-header">
                  <span>Savol - {i + 1}</span>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      className="remove-question"
                      onClick={() => {
                        setQuestions((prev) => prev.filter((_, idx) => idx !== i));
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>

                <div className="question-text-with-upload">
                  <textarea
                    className="q-text"
                    value={q.question}
                    placeholder="Savol matnini yozing..."
                    onChange={(e) => handleQuestionChange(i, "question", e.target.value)}
                  />

                  <div
                    className="inline-img-upload"
                    onClick={() => document.getElementById(`fileInput-${i}`).click()}
                  >
                    {q.image ? (
                      <>
                        <img src={URL.createObjectURL(q.image)} className="mini-preview" alt="" />
                        <span
                          className="remove-img-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuestionChange(i, "image", null);
                          }}
                        >
                          <RxCross2 size={14} />
                        </span>
                      </>
                    ) : (
                      <RiUploadCloudLine />
                    )}
                  </div>

                  <input
                    id={`fileInput-${i}`}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleQuestionChange(i, "image", e.target.files[0])}
                  />
                </div>

                <div className="options-list">
                  {q.options.map((opt, j) => (
                    <div key={j} className="option-row">
                      <input
                        type="text"
                        className="opt-input"
                        placeholder={`Variant ${j + 1}`}
                        value={opt}
                        onChange={(e) => handleOptionChange(i, j, e.target.value)}
                      />
                      <input
                        type="radio"
                        name={`correct-${i}`}
                        checked={q.correct === j}
                        onChange={() => handleCorrectChange(i, j)}
                      />
                      <span
                        className="remove-opt"
                        onClick={() => {
                          setQuestions((prev) => {
                            const updated = [...prev];
                            updated[i].options.splice(j, 1);
                            if (updated[i].correct >= updated[i].options.length) {
                              updated[i].correct = 0;
                            }
                            return updated;
                          });
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="add-option-btn"
                  onClick={() => {
                    setQuestions((prev) => {
                      const updated = [...prev];
                      updated[i].options.push("");
                      return updated;
                    });
                  }}
                >
                  + Variant qo‘shish
                </button>
              </motion.div>
            ))}

            <div className="form-actions">
              <motion.button
                type="button"
                className="save-btn"
                onClick={addQuestion}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                + Savol qo‘shish
              </motion.button>

              <motion.button
                type="button"
                className="save-btn"
                onClick={handleSubmit}
                disabled={loading || questions.some((q) => !q.question.trim())}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {loading ? "Saqlanmoqda..." : isEditMode ? "Yangilash" : "Saqlash va yuborish"}
              </motion.button>

              <motion.button
                type="button"
                className="cancel-btn"
                onClick={handlePrev}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Orqaga
              </motion.button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </PanelLayout>
  );
};

export default AddTestPage;

// FormInput komponenti
const FormInput = ({ label, name, value, onChange, placeholder, required }) => (
  <div className="form-group">
    <label>
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </label>
    <input
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
    />
  </div>
);